import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { Alert, Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTokenMaker } from "../../../hooks";
import {
  PredefinedObstacle,
  usePredefinedObstacle,
} from "../../../hooks/usePredefinedObstacle";
import { getTokenMakerUrl } from "../../../utils";
import { ConfirmationDialog } from "../../../widgets/ConfirmationDialog";
import { ObstacleEditDialog } from "./components/ObstacleEditDialog";
import { ObstaclePreview } from "./components/ObstaclePreview";
import { ObstacleTable } from "./components/ObstacleTable";

export interface EditFormData {
  name: string;
  width: string;
  length: string;
  height: string;
  image?: File;
}

export const MovableObstacleAdmin: React.FC = () => {
  const {
    loading,
    error: hookError,
    predefinedObstacles,
    createPredefinedObstacle,
    updatePredefinedObstacle,
    deletePredefinedObstacle,
    refreshObstacles,
  } = usePredefinedObstacle();
  const tokenMakerURL = getTokenMakerUrl();
  const { url: webURL } = useTokenMaker();
  
  // Check if we're on production
  const isProduction = window.location.hostname === 'app.airplx.com';
  const [selectedObstacle, setSelectedObstacle] =
    useState<PredefinedObstacle | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editingObstacle, setEditingObstacle] =
    useState<PredefinedObstacle | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [confirmDeploy, setConfirmDeploy] = useState<boolean>(false);
  const [newObstacleId, setNewObstacleId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Combine errors
  const error = componentError || hookError;

  // Focus container on mount and select first obstacle
  useEffect(() => {
    containerRef.current?.focus();
    
    // If we have a new obstacle ID to select after refresh
    if (newObstacleId && predefinedObstacles) {
      const index = predefinedObstacles.findIndex(o => o.id === newObstacleId);
      if (index !== -1) {
        setSelectedObstacle(predefinedObstacles[index]);
        setSelectedIndex(index);
        setNewObstacleId(null); // Clear after selection
        return;
      }
    }
    
    // Default behavior: select first obstacle if none selected
    if (
      predefinedObstacles &&
      predefinedObstacles.length > 0 &&
      !selectedObstacle
    ) {
      setSelectedObstacle(predefinedObstacles[0]);
      setSelectedIndex(0);
    }
  }, [predefinedObstacles, newObstacleId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle keyboard events when dialog is open
      if (editingObstacle || isCreating) return;
      if (!predefinedObstacles || predefinedObstacles.length === 0) return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        let newIndex = selectedIndex;

        if (newIndex === null) {
          newIndex = 0;
        } else if (e.key === "ArrowDown") {
          newIndex = Math.min(newIndex + 1, predefinedObstacles.length - 1);
        } else {
          newIndex = Math.max(newIndex - 1, 0);
        }

        setSelectedIndex(newIndex);
        setSelectedObstacle(predefinedObstacles[newIndex]);
        setImageError(false);

        // Scroll into view
        if (tableRef.current) {
          const row = tableRef.current.querySelector(
            `tbody tr:nth-child(${newIndex + 1})`
          );
          row?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [predefinedObstacles, selectedIndex, editingObstacle, isCreating]);

  const handleSelectObstacle = (
    obstacle: PredefinedObstacle,
    index: number
  ) => {
    setSelectedObstacle(obstacle);
    setSelectedIndex(index);
    setImageError(false);
  };

  const handleEditClick = (
    obstacle: PredefinedObstacle,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setEditingObstacle(obstacle);
    setIsCreating(false);
  };

  const handleAddClick = () => {
    setEditingObstacle(null);
    setIsCreating(true);
  };

  const handleDeleteClick = async (
    obstacle: PredefinedObstacle,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${obstacle.name}"?`)) {
      try {
        await deletePredefinedObstacle(obstacle.id);
        if (selectedObstacle?.id === obstacle.id) {
          setSelectedObstacle(null);
          setSelectedIndex(null);
        }
      } catch (err) {
        setComponentError(`Failed to delete obstacle: ${err}`);
      }
    }
  };

  // Helper function for dimension validation
  const validateDimension = (value: string) => {
    if (!value) return null;
    const num = parseFloat(value);
    return !isNaN(num) && num > 0 && num <= 1000 ? num : null;
  };

  const handleSaveEdit = async (formData: EditFormData) => {

    try {
      if (isCreating) {
        // Create new obstacle
        const width = validateDimension(formData.width);
        const length = validateDimension(formData.length);
        const height = validateDimension(formData.height);

        if (!formData.name || !width || !length || !height) {
          setComponentError("Please provide valid values for all fields. Dimensions must be positive numbers up to 1000 feet.");
          return;
        }

        const newObstacle = await createPredefinedObstacle({
          name: formData.name,
          width,
          length,
          height,
        });

        // Check if creation was successful
        if (!newObstacle) {
          setComponentError("Failed to create obstacle. Please try again.");
          return;
        }

        // Set the new obstacle ID for selection after refresh
        setNewObstacleId(newObstacle.id);

        // Upload image if provided
        if (formData.image) {
          await uploadObstacleImage(newObstacle.id, formData.image);
          // Refresh to get the updated image
          await refreshObstacles();
          // The new obstacle will be selected via useEffect
        } else {
          // If no image, still refresh to get the new obstacle in the list
          await refreshObstacles();
        }
      } else if (editingObstacle) {
        // Update existing obstacle
        const width = validateDimension(formData.width);
        const length = validateDimension(formData.length);
        const height = validateDimension(formData.height);

        // Check if any provided dimensions are invalid
        if (formData.width && !width) {
          setComponentError("Width must be a positive number up to 1000 feet.");
          return;
        }
        if (formData.length && !length) {
          setComponentError("Length must be a positive number up to 1000 feet.");
          return;
        }
        if (formData.height && !height) {
          setComponentError("Height must be a positive number up to 1000 feet.");
          return;
        }

        const updatedObstacle: PredefinedObstacle = {
          ...editingObstacle,
          name: formData.name || editingObstacle.name,
          width: width ?? editingObstacle.width,
          length: length ?? editingObstacle.length,
          height: height ?? editingObstacle.height,
        };

        await updatePredefinedObstacle(updatedObstacle);

        // Upload image if provided
        if (formData.image) {
          await uploadObstacleImage(editingObstacle.id, formData.image);
          // Refresh to get the updated image
          await refreshObstacles();
        }

        // Update selected obstacle if it was edited
        const updated = predefinedObstacles.find(
          (o) => o.id === editingObstacle.id
        );
        if (updated && selectedObstacle?.id === editingObstacle.id) {
          setSelectedObstacle(updated);
        }
      }

      setEditingObstacle(null);
      setIsCreating(false);
      setComponentError(null);
    } catch (err) {
      setComponentError(`Failed to save obstacle: ${err}`);
    }
  };

  const uploadObstacleImage = async (obstacleId: string, file: File) => {
    const formData = new FormData();
    formData.append("image", file);

    const response = await fetch(
      `${tokenMakerURL}/movable_obstacles/${obstacleId}/image`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to upload image");
    }
  };

  // Helper to prepare obstacle data for export
  const prepareObstacleExportData = (): {
    headers: string[];
    rows: string[][];
  } => {
    const headers = ["Name", "Width", "Length", "Height", "ID"];

    const rows = predefinedObstacles.map((o) => [
      o.name || "",
      o.width?.toFixed(1) || "",
      o.length?.toFixed(1) || "",
      o.height?.toFixed(1) || "",
      o.id,
    ]);

    return { headers, rows };
  };

  // Helper to download file
  const downloadFile = (
    content: string,
    filename: string,
    mimeType: string
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const { headers, rows } = prepareObstacleExportData();

    // Convert to TSV format
    const tsvContent = [
      headers.join("\t"),
      ...rows.map((row) => row.join("\t")),
    ].join("\n");

    const filename = `movable_obstacles_${
      new Date().toISOString().split("T")[0]
    }.tsv`;
    downloadFile(
      tsvContent,
      filename,
      "text/tab-separated-values;charset=utf-8;"
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading obstacles...</Typography>
      </Box>
    );
  }

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setComponentError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      sx={{
        p: 2,
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }}
    >
      <Stack spacing={3} sx={{ height: "100%" }}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              color="primary"
            >
              Add Obstacle
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={predefinedObstacles.length === 0}
              color="primary"
            >
              Export Obstacle List
            </Button>
            <Tooltip 
              title={!isProduction ? "Deploy is only available on production (app.airplx.com)" : ""}
              arrow
            >
              <span>
                <Button
                  onClick={() => setConfirmDeploy(true)}
                  variant="contained"
                  color="primary"
                  startIcon={<RocketLaunchIcon />}
                  disabled={!isProduction}
                >
                  Deploy
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ flex: 1, minHeight: 0, pr: { xs: 0, md: "13px" } }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <ObstacleTable
              obstacles={predefinedObstacles}
              selectedObstacle={selectedObstacle}
              onSelectObstacle={handleSelectObstacle}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
              tableRef={tableRef}
            />
          </Box>

          <ObstaclePreview
            selectedObstacle={selectedObstacle}
            imageError={imageError}
            onImageError={() => setImageError(true)}
          />
        </Stack>
      </Stack>

      <ObstacleEditDialog
        open={!!editingObstacle || isCreating}
        obstacle={editingObstacle}
        isCreating={isCreating}
        onClose={() => {
          setEditingObstacle(null);
          setIsCreating(false);
        }}
        onSave={handleSaveEdit}
      />

      {confirmDeploy && (
        <ConfirmationDialog
          title="Confirm deploy"
          text="Are you sure you want to deploy?"
          onCancel={() => setConfirmDeploy(false)}
          onClose={() => setConfirmDeploy(false)}
          onOk={async () => {
            await fetch(`${webURL}/jobs/update-movable-obstacles`);
            setConfirmDeploy(false);
          }}
          okText="Go baby go!"
        />
      )}
    </Box>
  );
};
